import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Collapse,
  IconButton,
  useMediaQuery,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import type { AuditLog } from '@byte-store-control/shared';
import { PageHeader } from '../../components/PageHeader';
import { ActionBadge } from '../../components/AuditHistory';
import { useAuditLogs } from '../../hooks/useAuditLogs';
import { formatDateGroup, groupLogsByDate } from '../../utils/formatTime';
import { AuditDetailPanel } from '../../components/AuditHistory/AuditDetailPanel';
import { MobileSubPageHeader } from '../../components/Mobile';
import { DEFAULT_STORE_FUNCTION } from '../../components/StoreManagement/storeDetailTabs';

type DateRangeOption = 'today' | 'last7days' | 'last30days';

const DATE_RANGE_OPTIONS: { value: DateRangeOption; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'last7days', label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
];

function getDateRangeValues(dateRange: DateRangeOption): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const endDate = now.toISOString();

  switch (dateRange) {
    case 'today': {
      const startOfToday = new Date(now);
      startOfToday.setHours(0, 0, 0, 0);
      return { startDate: startOfToday.toISOString(), endDate };
    }
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { startDate: start.toISOString(), endDate };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate };
    }
  }
}

interface ActivityItemProps {
  log: AuditLog;
  onStoreClick: (storeNumber: string) => void;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ log, onStoreClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDetails = Boolean(log.changes || log.affectedStores || log.entityId || log.ipAddress);

  return (
    <>
      <ListItem
        sx={{
          cursor: hasDetails ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={hasDetails ? () => setIsExpanded(!isExpanded) : undefined}
        secondaryAction={
          hasDetails && (
            <IconButton edge="end" size="small" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )
        }
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {new Date(log.timestamp).toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </Typography>
        </ListItemIcon>
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ActionBadge action={log.action} size="small" />
              {log.storeNumber && (
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStoreClick(log.storeNumber);
                  }}
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Store #{log.storeNumber}
                </Link>
              )}
            </Stack>
          }
          secondary={
            log.entityId && (
              <Typography variant="caption" color="text.secondary">
                {log.entityId}
              </Typography>
            )
          }
        />
      </ListItem>
      <Collapse in={isExpanded}>
        <Box sx={{ ml: 5 }}>
          <AuditDetailPanel log={log} />
        </Box>
      </Collapse>
    </>
  );
};

interface MyActivityPageProps {
  onBack?: () => void;
}

export const MyActivityPage: React.FC<MyActivityPageProps> = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRangeOption>('last7days');

  const dateRangeValues = useMemo(() => getDateRangeValues(dateRange), [dateRange]);

  const { logs, isLoading, error, loadMore, hasMore } = useAuditLogs({
    isMyActivity: true,
    startDate: dateRangeValues.startDate,
    endDate: dateRangeValues.endDate,
    autoFetch: true,
  });

  // Group logs by date
  const groupedLogs = useMemo(() => {
    const groups = groupLogsByDate(logs);
    // Convert to array and sort by date (newest first)
    return Array.from(groups.entries()).sort(
      ([a], [b]) => new Date(b).getTime() - new Date(a).getTime()
    );
  }, [logs]);

  const handleStoreClick = (storeNumber: string) => {
    navigate(`/stores/${storeNumber}/${DEFAULT_STORE_FUNCTION}`);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/');
    }
  };

  const renderContent = () => (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Date Filter Chips */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {DATE_RANGE_OPTIONS.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              onClick={() => setDateRange(option.value)}
              variant={dateRange === option.value ? 'filled' : 'outlined'}
              color={dateRange === option.value ? 'primary' : 'default'}
              size="small"
              sx={{ fontWeight: dateRange === option.value ? 600 : 400 }}
            />
          ))}
        </Stack>
      </Box>

      {isLoading && logs.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && logs.length === 0 && (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No activity found
          </Typography>
        </Box>
      )}

      {groupedLogs.map(([dateKey, dayLogs]) => (
        <Box key={dateKey} sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontWeight: 600,
              mb: 1,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
            }}
          >
            {formatDateGroup(dayLogs[0].timestamp)}
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <List disablePadding dense>
              {dayLogs.map((log, logIndex) => (
                <React.Fragment key={log.id}>
                  {logIndex > 0 && <Divider sx={{ mx: 2 }} />}
                  <ActivityItem log={log} onStoreClick={handleStoreClick} />
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Box>
      ))}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Chip
            label="Load More"
            onClick={loadMore}
            clickable
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {isLoading && logs.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
        <MobileSubPageHeader
          title="My Activity"
          subtitle="Your recent changes"
          onBack={handleBack}
        />
        <Box sx={{ flex: 1, px: 2, pb: 4 }}>{renderContent()}</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="My Activity" description="Your recent actions across all stores" />
      {renderContent()}
    </Box>
  );
};
